import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  forgeApi,
  type ForgePatternsResponse,
  type ForgePatternMode,
} from '../api/forge';

/**
 * useForgePatterns — TanStack Query hook for the Workspace-V2 FORGE Lane.
 *
 * Wraps `GET /api/v1/forge/patterns?mode=<mode>` (see `backend/api/forge_routes.py`).
 * Default mode is `'world'` — the only mode sprint-5a ships. The `mode`
 * param becomes meaningful when sprint-5b + mode-filtering land.
 *
 * Cache shape matches other workspace hooks (30s staleTime, 60s refetch) so
 * the lane refreshes passively in the background while staying cheap.
 */
export function useForgePatterns(
  mode: ForgePatternMode = 'world',
  limit = 40,
): UseQueryResult<ForgePatternsResponse> {
  return useQuery({
    queryKey: ['forge', 'patterns', mode, limit] as const,
    queryFn: () => forgeApi.getPatterns(mode, limit),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    // The endpoint is auth-gated (forge_routes.py, deliberate OWASP-A01);
    // in the no-token viewing posture it 401s deterministically. Fail fast
    // to the consumers' "FORGE patterns unavailable" states instead of
    // retry-looping on "Loading FORGE patterns…" (bug 20260609-93f00f).
    retry: false,
  });
}
